import { resolveSplitRuleSet } from './splitRuleSet.js';
import { displaySetContextFor, withInstances } from './displaySetContext.js';
import { sortInstancesCopy } from './sortInstances.js';
import { hasNoValue, isUnsafeKey } from '../safeFunctions/index.js';
export function computeSeriesFacts(instances, rules) {
    const facts = {};
    for (const rule of rules) {
        if (!rule.series) {
            continue;
        }
        const computed = rule.series({ instances, series: facts }) ?? {};
        for (const [name, value] of Object.entries(computed)) {
            if (!isUnsafeKey(name) &&
                !Object.prototype.hasOwnProperty.call(facts, name)) {
                facts[name] = value;
            }
        }
    }
    return facts;
}
export function chainComparators(comparators) {
    if (comparators.length === 1) {
        return comparators[0];
    }
    return (a, b, context) => {
        for (const comparator of comparators) {
            const order = comparator(a, b, context);
            if (Number.isFinite(order) && order !== 0) {
                return order;
            }
        }
        return 0;
    };
}
function buildInstanceOrderer(splitRule, context, options) {
    const comparators = [
        splitRule.compareInstances,
        options.compareInstances,
    ].filter((comparator) => typeof comparator === 'function');
    const baseSort = options.sortInstances;
    const orderContext = {
        matchedRule: splitRule,
        series: context.series,
    };
    return (instances) => {
        let ordered = sortInstancesCopy(instances);
        if (baseSort) {
            ordered = [...baseSort(ordered, orderContext)];
        }
        if (comparators.length) {
            const compare = chainComparators(comparators);
            const sortContext = {
                ...context,
                displaySet: displaySetContextFor(context.series, ordered),
            };
            ordered.sort((a, b) => compare(a, b, sortContext));
        }
        return ordered;
    };
}
export function orderInstancesForRule(instances, splitRule, options = {}) {
    const context = {
        series: options.series
            ? withInstances(options.series, instances)
            : computeSeriesFacts(sortInstancesCopy(instances), [splitRule]),
    };
    return buildInstanceOrderer(splitRule, context, options)(instances);
}
function isDigitAt(value, index) {
    const code = value.charCodeAt(index);
    return code >= 0x30 && code <= 0x39;
}
function digitRunEnd(value, start) {
    let end = start;
    while (end < value.length && isDigitAt(value, end)) {
        end += 1;
    }
    return end;
}
function compareDigitRuns(a, b) {
    const aValue = a.replace(/^0+/, '');
    const bValue = b.replace(/^0+/, '');
    if (aValue.length !== bValue.length) {
        return aValue.length - bValue.length;
    }
    if (aValue !== bValue) {
        return aValue < bValue ? -1 : 1;
    }
    return a.length - b.length;
}
function compareSplitKeys(a, b) {
    let aIndex = 0;
    let bIndex = 0;
    while (aIndex < a.length && bIndex < b.length) {
        if (isDigitAt(a, aIndex) && isDigitAt(b, bIndex)) {
            const aEnd = digitRunEnd(a, aIndex);
            const bEnd = digitRunEnd(b, bIndex);
            const byDigits = compareDigitRuns(a.slice(aIndex, aEnd), b.slice(bIndex, bEnd));
            if (byDigits !== 0) {
                return byDigits;
            }
            aIndex = aEnd;
            bIndex = bEnd;
            continue;
        }
        const aCode = a.charCodeAt(aIndex);
        const bCode = b.charCodeAt(bIndex);
        if (aCode !== bCode) {
            return aCode - bCode;
        }
        aIndex += 1;
        bIndex += 1;
    }
    return a.length - aIndex - (b.length - bIndex);
}
export function normalizeSplitKeyPart(value) {
    if (hasNoValue(value)) {
        return null;
    }
    if (Array.isArray(value)) {
        const parts = value.map(normalizeSplitKeyPart);
        return parts.length === 1 ? parts[0] : parts;
    }
    if (typeof value === 'string') {
        const number = Number(value);
        return Number.isFinite(number) && String(number) === value ? number : value;
    }
    return value;
}
function buildGroupByParts(instance, context, splitRule) {
    const groupBy = splitRule.groupBy ?? ['SeriesInstanceUID'];
    return groupBy.map((key) => normalizeSplitKeyPart(typeof key === 'function' ? key(instance, context) : instance[key]));
}
function buildSplitKey(ruleDiscriminator, baseKeyParts) {
    return JSON.stringify([ruleDiscriminator, ...baseKeyParts]);
}
export function groupInstancesBySplitRules(instances, ruleSet, onUnmatched, options = {}) {
    const splitRules = resolveSplitRuleSet(ruleSet);
    if (!instances.length) {
        return [];
    }
    const sorted = sortInstancesCopy(instances);
    const context = {
        series: computeSeriesFacts(sorted, splitRules),
    };
    const claimedByRule = splitRules.map(() => []);
    for (const instance of sorted) {
        let matched = false;
        for (let ruleIndex = 0; ruleIndex < splitRules.length; ruleIndex++) {
            const splitRule = splitRules[ruleIndex];
            if (splitRule.matches && !splitRule.matches(instance, context)) {
                continue;
            }
            claimedByRule[ruleIndex].push(instance);
            matched = true;
            break;
        }
        if (!matched) {
            onUnmatched?.(instance);
        }
    }
    const ruleOrderers = splitRules.map((rule) => buildInstanceOrderer(rule, context, options));
    const instancesMap = new Map();
    const groupRuleIndex = new Map();
    for (let ruleIndex = 0; ruleIndex < splitRules.length; ruleIndex++) {
        const splitRule = splitRules[ruleIndex];
        for (const instance of claimedByRule[ruleIndex]) {
            const key = buildSplitKey(splitRule.id, buildGroupByParts(instance, context, splitRule));
            let group = instancesMap.get(key);
            if (!group) {
                group = {
                    instances: [],
                    matchedRule: splitRule,
                    series: context.series,
                    splitKey: key,
                };
                instancesMap.set(key, group);
                groupRuleIndex.set(key, ruleIndex);
            }
            group.instances.push(instance);
        }
    }
    const groups = Array.from(instancesMap.values());
    for (const group of groups) {
        const ruleIndex = groupRuleIndex.get(group.splitKey ?? '') ?? 0;
        group.instances = ruleOrderers[ruleIndex](group.instances);
    }
    return groups.sort((a, b) => {
        const aKey = a.splitKey ?? '';
        const bKey = b.splitKey ?? '';
        const ruleOrder = (groupRuleIndex.get(aKey) ?? 0) - (groupRuleIndex.get(bKey) ?? 0);
        return ruleOrder !== 0 ? ruleOrder : compareSplitKeys(aKey, bKey);
    });
}
